import React, { Component } from 'react';
import { Cta } from './Cta';
import { Reveal } from './Reveal';
import { PathThread } from './PathThread';
import { useScore } from './ScoreContext';
import { Compass, Route, Footprints, DoorOpen, Eye, PenLine } from 'lucide-react';
export function Offer() {
  const {
    answeredCount
  } = useScore();
  const ctaLabel = answeredCount === 0 ? 'Get Your Reader-to-Revenue Score' : answeredCount < 4 ? 'Continue Your Score' : 'Finish Your Score';
  const handleCtaClick = () => {
    document.getElementById('final-score-block')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buildComponents = [{
    label: 'Reader diagnostic',
    icon: Compass
  }, {
    label: 'Guided path',
    icon: Route
  }, {
    label: 'Useful next steps',
    icon: Footprints
  }, {
    label: 'Offer paths',
    icon: DoorOpen
  }, {
    label: 'What readers cared about',
    icon: Eye
  }, {
    label: 'Content and sales assets',
    icon: PenLine
  }];
  const futureQuestions = ['Course', 'Certification', 'Workshop', 'Event', 'Advisory', 'Team implementation', 'Sales system', 'Content system'];
  const proof = [{
    title: 'Reader sees where they are',
    Visual: () => <div className="flex items-center justify-center gap-2 w-full" data-id="element-311" data-mp="zhvbueyxhyt7">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-312" data-mp="zgk98ih7yx3o"></div>
          <div className="h-px w-8 bg-gray-200" data-id="element-313" data-mp="13jglzsk2vyzp"></div>
          <div className="w-4 h-4 rounded-full bg-seaglass ring-4 ring-seaglass/20" data-id="element-314" data-mp="6u99fa1ft07x"></div>
          <div className="h-px w-8 bg-gray-200" data-id="element-315" data-mp="bski3hfzx4ok"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-316" data-mp="btc0fqkl15ww"></div>
        </div>
  }, {
    title: 'Reader gets a useful next step',
    Visual: () => <div className="flex items-center justify-center gap-3 w-full" data-id="element-317" data-mp="kkvwu61qz3op">
          <div className="w-3 h-3 rounded-full bg-seaglass" data-id="element-318" data-mp="kn8nr8phgodw"></div>
          <div className="h-1 flex-1 max-w-[70px] rounded-full bg-seaglass" data-id="element-319" data-mp="3k7ui8k0brztp"></div>
          <div className="px-3 py-1.5 rounded-lg bg-sand/15 border border-sand text-xs font-semibold text-sand-dark" data-id="element-320" data-mp="3o9fohmictfux">
            Next step
          </div>
        </div>
  }, {
    title: 'Reader applies the idea',
    Visual: () => <div className="flex items-center justify-center gap-3 w-full" data-id="element-321" data-mp="qg4j1qfd5sic">
          <div className="w-9 h-11 rounded-md bg-navy" data-id="element-322" data-mp="155to3wqkugnw"></div>
          <div className="flex flex-col gap-1.5" data-id="element-323" data-mp="157d7hw7j57tw">
            <div className="h-1.5 w-14 rounded-full bg-seaglass/50" data-id="element-324" data-mp="107s51bt5ihcf"></div>
            <div className="h-1.5 w-10 rounded-full bg-seaglass/40" data-id="element-325" data-mp="cgczla62vjwy"></div>
            <div className="h-1.5 w-12 rounded-full bg-seaglass/30" data-id="element-326" data-mp="7hrx143cso5c"></div>
          </div>
        </div>
  }, {
    title: 'You see what mattered',
    Visual: () => <div className="flex items-center justify-center gap-2 w-full" data-id="element-327" data-mp="licfu099qjyu">
          {[6, 10, 7, 12, 8].map((h, i) => <div key={i} className={`w-2.5 rounded-full ${i === 3 ? 'bg-seaglass' : 'bg-gray-200'}`} style={{
        height: `${h * 3}px`
      }} data-id="element-328" data-mp="heehvsvojf7d" data-mpi={i}>
      </div>)}
        </div>
  }];
  return <section className="py-24 bg-paper relative" data-id="element-329" data-mp="1su360nko6k9r">
      <div className="absolute -top-12 left-1/2 -translate-x-1/2" data-id="element-330" data-mp="1sxh4vlu4kbcg">
        <PathThread data-id="element-331" data-mp="20d2omr0b47s2" />
      </div>
      <div className="max-w-5xl mx-auto px-6 space-y-32" data-id="element-332" data-mp="25dqntzyovuxd">
        {/* 8A: The Build — system hub */}
        <div data-id="element-333" data-mp="1qghho04q8mll">
          <Reveal data-id="element-334" data-mp="1qgpwvyobo3zy">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 text-center leading-tight" data-id="element-335" data-mp="1qhj4t2z0ozjo">
              The build:{' '}
              <span className="italic text-seaglass" data-id="element-336" data-mp="1vfsz7bcx3w48">Reader-to-Revenue</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} data-id="element-337" data-mp="2j7851e3wkvk8">
            <div className="text-lg text-text-muted leading-relaxed max-w-2xl mx-auto text-center mb-16 space-y-5" data-id="element-338" data-mp="2o3yjjx027j3j">
              <p data-id="element-339" data-mp="29g3rmitvliri">
                A Reader-to-Revenue Build is the done-for-you system behind the
                book.
              </p>
              <p data-id="element-340" data-mp="2eclwdip7hh3u">
                It gives serious readers a useful next step after the book
                creates trust.
              </p>
              <p data-id="element-341" data-mp="36ug3bc2wz4cr">
                It helps them raise their hand before they are ready for a sales
                call.
              </p>
              <p data-id="element-342" data-mp="1j764dh6d60gn">
                And it helps you see what they cared about, what they may need,
                and where they might fit.
              </p>
              <p className="font-serif text-2xl text-navy pt-3" data-id="element-343" data-mp="197yrev3jk7u2">
                The point is not to turn the book into a funnel.
                <br className="hidden md:block" data-id="element-344" data-mp="14afb7pna9kzh" />
                The point is to give serious readers a better next step than
                "buy the book," "join my list," or "book a call."
              </p>
            </div>
          </Reveal>

          {/* Hub and spokes */}
          <Reveal delay={0.2} data-id="element-345" data-mp="1s6aymbaw77n0">
            <div className="relative" data-id="element-346" data-mp="1s5hz9ve9jkew">
              {/* Desktop: 3 left / hub / 3 right */}
              <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-0" data-id="element-347" data-mp="1n781dd15m5pl">
                <div className="flex flex-col gap-6 items-end" data-id="element-348" data-mp="2ko5h91xyqjbr">
                  {buildComponents.slice(0, 3).map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-0 w-full justify-end" data-id="element-349" data-mp="2o5t9gxmwjhkt">
                    
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl pl-4 pr-5 py-3.5 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-350" data-mp="2s7xvba73z9ww">
                        <span className="w-8 h-8 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-351" data-mp="324srhmfwsy80">
                          <Icon className="w-4 h-4" data-id="element-352" data-mp="373nnjq2w0ja2" />
                        </span>
                        <span className="text-sm font-medium text-navy text-right" data-id="element-353" data-mp="3b4pdthettkzw">
                          {label}
                        </span>
                      </div>
                      <div className="w-8 h-px bg-seaglass/40" data-id="element-354" data-mp="3kw3jk6xdfh1h"></div>
                    </div>)}
                </div>

                <div className="px-2" data-id="element-355" data-mp="3pvg76tw2gs54">
                  <div className="w-44 h-44 rounded-full bg-navy text-paper flex flex-col items-center justify-center text-center px-6 border-4 border-seaglass shadow-[0_24px_50px_-20px_rgba(26,40,56,0.5)]" data-id="element-356" data-mp="3psk64wox9ncg">
                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-seaglass-light mb-1.5" data-id="element-357" data-mp="3539jjtygtbwi">
                      The system
                    </span>
                    <span className="font-serif text-xl leading-tight" data-id="element-358" data-mp="3ew7405n0paxr">
                      Reader-to-Revenue Build
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-6 items-start" data-id="element-359" data-mp="3nusldxgr6jwy">
                  {buildComponents.slice(3).map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-0 w-full" data-id="element-360" data-mp="1pf6eozvixce">
                      <div className="w-8 h-px bg-seaglass/40" data-id="element-361" data-mp="3svgagdlf8qoj"></div>
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl pl-4 pr-5 py-3.5 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-362" data-mp="3st3v8871suf6">
                        <span className="w-8 h-8 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-363" data-mp="1yigzwyvwemo4">
                          <Icon className="w-4 h-4" data-id="element-364" data-mp="e1hasee1qq0c" />
                        </span>
                        <span className="text-sm font-medium text-navy" data-id="element-365" data-mp="j0aoo7x8wj11">
                          {label}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Mobile: hub on top, connector, 2-col component grid */}
              <div className="md:hidden flex flex-col items-center" data-id="element-366" data-mp="3rg46cq3bc0jo">
                <div className="w-40 h-40 rounded-full bg-navy text-paper flex flex-col items-center justify-center text-center px-6 border-4 border-seaglass shadow-[0_20px_40px_-18px_rgba(26,40,56,0.5)]" data-id="element-367" data-mp="3vge9zw3muevd">
                  <span className="text-[0.6rem] uppercase tracking-[0.2em] text-seaglass-light mb-1.5" data-id="element-368" data-mp="3mfgek279ffoe">
                    The system
                  </span>
                  <span className="font-serif text-lg leading-tight" data-id="element-369" data-mp="1ko3bzjsrljvi">
                    Reader-to-Revenue Build
                  </span>
                </div>
                <div className="w-px h-8 bg-seaglass/40" data-id="element-370" data-mp="7itesji5uspp"></div>
                <div className="grid grid-cols-2 gap-3 w-full" data-id="element-371" data-mp="3getlsr0rgv9">
                  {buildComponents.map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-2xl px-3.5 py-3 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-372" data-mp="3puy9xuzk164d">
                    
                      <span className="w-7 h-7 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-373" data-mp="3fws8tflko40k">
                        <Icon className="w-3.5 h-3.5" data-id="element-374" data-mp="3bvq80uc0bjwh" />
                      </span>
                      <span className="text-xs font-medium text-navy leading-tight" data-id="element-375" data-mp="36wvmhkmnnh8o">
                        {label}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* 8B: The Path After the Book Comes First */}
        <div data-id="element-376" data-mp="1cngrh16s6mgd">
          <Reveal data-id="element-377" data-mp="1cnq83y5xrh9e">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 text-center leading-tight" data-id="element-378" data-mp="1cm5kksi1gyl1">
              The path after the book <br data-id="element-379" data-mp="1md9s575dxh5u" />
              <span className="italic text-seaglass" data-id="element-380" data-mp="1meulwujvkw2n">usually comes first</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} data-id="element-381" data-mp="1rccw0o9mphc8">
            <p className="text-lg text-text-muted leading-relaxed max-w-2xl mx-auto text-center mb-16" data-id="element-382" data-mp="1repa2qc9n7k0">
              Once the path after the book is clear, bigger business questions
              become easier. Should this become a course? A certification? A
              workshop? An event? An advisory path? A team implementation model?
              A stronger sales system? A better content system?
            </p>
          </Reveal>

          <Reveal delay={0.2} data-id="element-383" data-mp="2f38hz4relibk">
            <div className="relative pt-8 pb-12" data-id="element-384" data-mp="2f1xjnkgq92ru">
              <div className="bg-paper-dark rounded-3xl p-10 pt-20 border border-gray-200 shadow-inner relative mt-12" data-id="element-385" data-mp="2j3j17j47i58d">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-paper-dark px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-text-muted border border-gray-200" data-id="element-386" data-mp="okqyykhlftpa">
                  Layer 2: What this can clarify next
                </div>
                <div className="flex flex-wrap justify-center gap-3 lg:grid lg:grid-cols-4 lg:gap-3" data-id="element-387" data-mp="13aex5cuhac8s">
                  {futureQuestions.map((q, idx) => <div key={idx} className="rounded-full border border-gray-100 bg-white px-4 py-2 text-center text-sm text-text-muted shadow-sm lg:px-2 lg:text-xs lg:whitespace-nowrap" data-id="element-388" data-mp="5o7ui8hqitz2" data-mpi={idx}>
                    
                      {q}
                    </div>)}
                </div>
              </div>

              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-md" data-id="element-389" data-mp="kkypm5kdljiv">
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_16px_30px_-10px_rgba(26,40,56,0.15)] relative z-10 text-center" data-id="element-390" data-mp="1qzhf6lt0y73t">
                  <div className="text-xs font-semibold uppercase tracking-wider text-seaglass mb-3" data-id="element-391" data-mp="3ghekjdcbsnqg">
                    Layer 1
                  </div>
                  <div className="font-serif text-xl text-navy" data-id="element-392" data-mp="3bitmnkkjqg56">
                    The path after the book
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* 8C: Product Proof — designed conceptual cards */}
        <div data-id="element-393" data-mp="2wugkg5uv8128">
          <Reveal data-id="element-394" data-mp="2wuyss0usy5qi">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-16 text-center leading-tight" data-id="element-395" data-mp="2wrayv0lg3hb9">
              What this can{' '}
              <span className="italic text-seaglass" data-id="element-396" data-mp="2ns7cnnqzelsj">look like</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-397" data-mp="2iu5st0lqeig0">
            {proof.map(({
            title,
            Visual
          }, idx) => <Reveal key={idx} delay={0.1 * idx} data-id="element-398" data-mp="m01duyb5livr" data-mpi={idx}>
                <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_12px_28px_-16px_rgba(26,40,56,0.15)] overflow-hidden h-full" data-id="element-399" data-mp="h29i1t7e1cma">
                  <div className="bg-paper-dark h-32 flex items-center justify-center px-8 border-b border-gray-100" data-id="element-400" data-mp="2bhpwz7cfzhuj">
                    <Visual data-id="element-401" data-mp="2gaj1pv7wuq6m" />
                  </div>
                  <div className="p-6" data-id="element-402" data-mp="21l3ayf8dqkqx">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-seaglass mb-2" data-id="element-403" data-mp="21m4wxeqxornf">
                      Conceptual
                    </div>
                    <div className="font-serif text-xl text-navy" data-id="element-404" data-mp="2ua0pc79gzxee">{title}</div>
                  </div>
                </div>
              </Reveal>)}
          </div>
        </div>

        {/* 8D: Fit / Not Fit */}
        <div data-id="element-405" data-mp="2ka05mk7ovf9o">
          <Reveal data-id="element-406" data-mp="2kajoq492s9pa">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-4 text-center leading-tight" data-id="element-407" data-mp="2kd5tjw4l0b3t">
              Built for expert-authors with <br data-id="element-408" data-mp="18v4pette95r0" />
              <span className="italic text-seaglass" data-id="element-409" data-mp="18ulcxsmij9kd">
                real work behind the book
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.05} data-id="element-410" data-mp="3s2i9tyh0casc">
            <p className="text-center text-text-muted mb-14 max-w-xl mx-auto" data-id="element-411" data-mp="3s1pavjoaipkr">
              A quick read on whether a build is worth your time.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-412" data-mp="4xiv9yzkrf4z">
            <Reveal delay={0.1} data-id="element-413" data-mp="4wzq7iv0wl8d">
              <div className="bg-white rounded-3xl p-9 md:p-10 border border-seaglass/40 shadow-[0_16px_36px_-20px_rgba(139,168,159,0.6)] h-full" data-id="element-414" data-mp="w7dgfmxzy7c">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass mb-2" data-id="element-415" data-mp="39csixmgd895p">
                  Good fit
                </div>
                <h3 className="text-2xl font-serif text-navy mb-7" data-id="element-416" data-mp="3i4aypw9egmy9">
                  This is for you if...
                </h3>
                <ul className="space-y-4 text-text" data-id="element-417" data-mp="3ddcggr4upesd">
                  {['Your book is already in the world', 'Your book supports a business, not just a royalty stream', 'You have a method, framework, point of view, or body of work', 'You sell, or want to sell, higher-value work', 'Your book points toward consulting, workshops, speaking, coaching, advisory, courses, certification, events, community, or team implementation'].map(item => <li key={item} className="flex gap-3.5 items-start" data-id="element-418" data-mp="33ba0nsxmhg9b">
                      <span className="w-6 h-6 rounded-full bg-seaglass text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-419" data-mp="389juo07m2atc">
                        ✓
                      </span>
                      <span className="leading-relaxed" data-id="element-420" data-mp="3hyv3dlql5dgp">{item}</span>
                    </li>)}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2} data-id="element-421" data-mp="3r0bejvp85jlz">
              <div className="bg-paper-dark rounded-3xl p-9 md:p-10 border border-gray-200 h-full" data-id="element-422" data-mp="3r01k9vx5cqah">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted mb-2" data-id="element-423" data-mp="3vycx8fiqngwx">
                  Not a fit
                </div>
                <h3 className="text-2xl font-serif text-navy mb-7" data-id="element-424" data-mp="1womx9mvit4yf">
                  This is not for you if...
                </h3>
                <ul className="space-y-4 text-text-muted" data-id="element-425" data-mp="1u241mabcjprm">
                  {['You only want to sell more books', 'You want bestseller help', 'You want PR or book ads', 'You want a cheap author website', 'You want a generic funnel', 'You have no real offer, method, or business behind the book'].map(item => <li key={item} className="flex gap-3.5 items-start" data-id="element-426" data-mp="1fdhngbxgh41z">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-427" data-mp="1fb4z9h13txgh">
                        ✕
                      </span>
                      <span className="leading-relaxed" data-id="element-428" data-mp="232acrpjkodnm">{item}</span>
                    </li>)}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        {/* 8E: Investment — spacious, premium, calm */}
        <Reveal data-id="element-429" data-mp="ro6jymoq0vk5">
          <div className="text-center bg-navy text-white rounded-[2.5rem] px-8 py-20 md:px-16 md:py-28 shadow-2xl relative overflow-hidden" data-id="element-430" data-mp="roon839swa1k">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-sand" data-id="element-431" data-mp="3auiptcw40vz0"></div>

            <div className="max-w-2xl mx-auto" data-id="element-432" data-mp="3asepf4xf6f3h">
              <p className="text-sm uppercase tracking-[0.22em] text-gray-400 mb-8" data-id="element-433" data-mp="3eut6pklfx6sn">
                Reader-to-Revenue Builds start at
              </p>
              <div className="font-serif font-semibold text-sand text-6xl md:text-8xl leading-none mb-14 tracking-[-0.02em]" data-id="element-434" data-mp="35v62ys58nowf">
                $12,500
              </div>

              <div className="text-lg text-gray-300 leading-relaxed space-y-5 mb-14" data-id="element-435" data-mp="2r4yth1x7lg7v">
                <p data-id="element-436" data-mp="2vyjja6onf3vz">This is not a low-ticket book funnel.</p>
                <p data-id="element-437" data-mp="2i63oft5wic65">It is not a prettier author website.</p>
                <p data-id="element-438" data-mp="2i3h56w9ne883">It is not a pile of bonus resources.</p>
                <p className="text-paper" data-id="element-439" data-mp="2n5q0w0dwnodw">
                  It is a business layer behind the authority your book already
                  created.
                </p>
                <p data-id="element-440" data-mp="2d67k1stakiq2">
                  The economics make sense when the book supports high-value
                  work: consulting, workshops, advisory, speaking, courses,
                  certification, events, or team implementation.
                </p>
              </div>

              <p className="text-xl md:text-2xl font-serif text-paper leading-snug mb-14" data-id="element-441" data-mp="1plgdatopqgny">
                The question is not whether every reader becomes revenue. They
                will not.{' '}
                <span className="italic text-sand" data-id="element-442" data-mp="1ym4qxna72f0b">
                  The question is whether serious readers deserve a better next
                  step than disappearing after the book.
                </span>
              </p>

              <Cta variant="primary" showArrow onClick={handleCtaClick} className="px-10 py-4 text-lg shadow-[0_14px_34px_-10px_rgba(212,165,116,0.6)] hover:-translate-y-0.5" data-id="element-443" data-mp="2m9w22nk00m00">
                
                {ctaLabel}
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}